import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * GALLERY — new variants (mosaic-asymmetric · violet-bg-masonry · filterable · with-swatches)
 * ════════════════════════════════════════════════════════════════════════ */
export function GalleryExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const layout = block.layout;
  const imgs = c.images || c.gallery || [];

  if (layout === 'mosaic-asymmetric') {
    const cells = imgs.length ? imgs.slice(0, 6) : Array.from({length:6});
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          {c.title && <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title}</H1>}
          <div className="mt-8 grid grid-cols-4 grid-rows-3 gap-3 h-[560px]">
            <div className="col-span-2 row-span-2">{cells[0] ? <SafeImg src={cells[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-rose-300 to-amber-300" />}</div>
            <div className="col-span-1 row-span-1">{cells[1] ? <SafeImg src={cells[1]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-cyan-300 to-violet-400" />}</div>
            <div className="col-span-1 row-span-1">{cells[2] ? <SafeImg src={cells[2]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-emerald-300 to-cyan-400" />}</div>
            <div className="col-span-2 row-span-1">{cells[3] ? <SafeImg src={cells[3]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-violet-300 to-pink-300" />}</div>
            <div className="col-span-2 row-span-1">{cells[4] ? <SafeImg src={cells[4]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-amber-300 to-rose-400" />}</div>
            <div className="col-span-2 row-span-1">{cells[5] ? <SafeImg src={cells[5]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-indigo-300 to-fuchsia-400" />}</div>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'violet-bg-masonry') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: '#3B1F8B' }}>
        <div className="max-w-6xl mx-auto text-white">
          <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold text-center mb-3" style={{ fontFamily: theme.headingFont }}>{c.title || 'More than 150 components'}</h2>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="text-center opacity-80 max-w-xl mx-auto" placeholder='Tagline (optional)' />
          <div className="mt-10 columns-2 md:columns-4 gap-3 [&>*]:mb-3 [&>*]:break-inside-avoid">
            {(imgs.length ? imgs.slice(0,8) : Array.from({length:8})).map((src,i) => (
              src ? <SafeImg key={i} src={src} alt="" className="w-full" /> : <div key={i} className="w-full h-40 bg-white/10" />
            ))}
          </div>
          {c.buttons?.[0] && <div className="text-center mt-10"><Btn block={block} onEdit={onEdit} text={c.buttons[0].text || 'View All'} link={c.buttons[0].link} primary buttonIndex={0} color={c.buttons[0].color} theme={{ ...theme, accent: '#2DD4BF', ink: '#fff' }} /></div>}
        </div>
      </section>
    );
  }

  if (layout === 'filterable') {
    const cats = c.categories || ['All', 'Photos', 'Videos', 'Branding'];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold text-center" style={{ fontFamily: theme.headingFont }}>{c.title || 'Better design for your content'}</h2>
          <div className="flex justify-center flex-wrap gap-2 mt-6">
            {cats.map((cat,i) => <button key={i} className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold ${i===0?'bg-violet-600 text-white':'border border-gray-300 text-gray-600'}`}>{cat}</button>)}
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
            {(imgs.length ? imgs.slice(0,6) : Array.from({length:6})).map((src,i)=>(
              <div key={i} className="aspect-[4/5]">
                {src ? <SafeImg src={src} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-400" />}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'with-swatches') {
    const items = c.items || Array.from({length:8}).map((_,i)=>({ name:`Item ${i+1}`, swatches:['#EF4444','#3B82F6','#10B981'], image:imgs[i] }));
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          {c.title && <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold text-center mb-10" style={{ fontFamily: theme.headingFont }}>{c.title}</h2>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {items.slice(0,8).map((it,i) => (
              <div key={i}>
                <div className="aspect-[3/4]">{it.image ? <SafeImg src={it.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-400" />}</div>
                <E block={block} onEdit={onEdit} field={`items.${i}.name`} value={it.name} tag='p' className="mt-3 text-sm font-bold" placeholder='Item name' />
                <div className="mt-1 flex gap-1.5">{(it.swatches||['#EF4444','#3B82F6']).map((s,si)=>(<span key={si} className="w-3 h-3 rounded-full border border-gray-300" style={{ background:s }} />))}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'alternating-rows') {
    const items = c.items || imgs.slice(0, 4).map((src, i) => ({ image: src, title: `Project ${i+1}`, description: 'Short project description.', link: '#' }));
    const data = (items.length ? items : Array.from({length:3}).map((_,i)=>({ title:`Project ${i+1}`, description:'Short description.' }))).slice(0,4);
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto space-y-16">
          {c.title && <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold text-center" style={{ fontFamily: theme.headingFont }}>{c.title}</h2>}
          {data.map((it, i) => (
            <div key={i} className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-center ${i % 2 ? 'md:[direction:rtl]' : ''}`}>
              <div className="aspect-[4/3] md:[direction:ltr]">{it.image ? <SafeImg src={it.image} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-violet-300 to-pink-400" />}</div>
              <div className="md:[direction:ltr]">
                <E block={block} onEdit={onEdit} field={`items.${i}.eyebrow`} value={it.eyebrow || `Case Study ${String(i+1).padStart(2,'0')}`} tag='p' className="text-xs uppercase tracking-[0.18em] opacity-60 font-bold" placeholder='Eyebrow' />
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='h3' className="mt-2 text-2xl md:text-3xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder='Project title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-3 text-sm opacity-75 leading-relaxed" placeholder='Description' />
                <a href={it.link || '#'} className="mt-4 inline-block text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>Learn More →</a>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (layout === 'last-works-link') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: '#3B1F8B' }}>
        <div className="max-w-5xl mx-auto text-white">
          <div className="flex items-end justify-between gap-4">
            <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }}>{c.title || 'Last Works'}</h2>
            <a href={(c.buttons && c.buttons[0]?.link) || '#'} className="text-sm font-bold uppercase tracking-wider hover:opacity-80" style={{ color: theme.accent || '#2DD4BF' }}>See All Works →</a>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-2 gap-5">
            {(imgs.length ? imgs.slice(0,4) : Array.from({length:4})).map((src,i) => (
              <div key={i}>
                <div className="aspect-[4/3] bg-white/5">{src ? <SafeImg src={src} alt="" className="w-full h-full object-cover" /> : <div className={`w-full h-full bg-gradient-to-br ${['from-amber-400 to-rose-500','from-cyan-400 to-violet-500','from-emerald-400 to-teal-600','from-fuchsia-400 to-pink-500'][i]}`} />}</div>
                <p className="mt-3 text-sm font-bold">Project {String(i+1).padStart(2,'0')}</p>
                <p className="text-xs opacity-60 uppercase tracking-wider">Branding · Digital</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'carousel-scroll') {
    const cells = imgs.length ? imgs : Array.from({length:6});
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="py-16 overflow-hidden" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto px-6 mb-8">
          {c.title && <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }}>{c.title}</h2>}
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-2 opacity-70" placeholder='Tagline (optional)' />
        </div>
        <div className="overflow-x-auto pl-6 md:pl-[max(1.5rem,calc((100vw-72rem)/2+1.5rem))] [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
          <div className="flex gap-4 pb-4 pr-6">
            {cells.slice(0,8).map((src,i) => (
              <div key={i} className="w-[280px] md:w-[340px] aspect-[4/3] flex-shrink-0 rounded-xl overflow-hidden">
                {src ? <SafeImg src={src} alt="" className="w-full h-full object-cover" /> : <div className={`w-full h-full bg-gradient-to-br ${['from-rose-400 to-amber-400','from-cyan-400 to-violet-500','from-emerald-400 to-teal-600','from-fuchsia-400 to-pink-500','from-indigo-400 to-violet-600','from-amber-300 to-orange-500','from-pink-300 to-fuchsia-400','from-sky-300 to-blue-500'][i % 8]}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'marquee-gallery') {
    // Infinite marquee scrolling — duplicate the cell list to make a seamless loop.
    const cells = imgs.length ? imgs : Array.from({length:6});
    const dup = [...cells, ...cells];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="py-16 overflow-hidden" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto px-6 mb-8 text-center">
          {c.title && <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }}>{c.title}</h2>}
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-2 opacity-70" placeholder='Tagline (optional)' />
        </div>
        <div className="relative">
          <style>{`@keyframes wb-marquee-x { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }`}</style>
          <div className="flex gap-4 w-max" style={{ animation: 'wb-marquee-x 40s linear infinite' }}>
            {dup.map((src, i) => (
              <div key={i} className="w-[300px] aspect-[4/3] flex-shrink-0 rounded-xl overflow-hidden">
                {src ? <SafeImg src={src} alt="" className="w-full h-full object-cover" /> : <div className={`w-full h-full bg-gradient-to-br ${['from-rose-400 to-amber-400','from-cyan-400 to-violet-500','from-emerald-400 to-teal-600','from-fuchsia-400 to-pink-500','from-indigo-400 to-violet-600','from-amber-300 to-orange-500'][i % 6]}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Gallery — NEW `masonry-editorial` ──
  // Column-based masonry with varied aspect ratios per image. Above the
  // grid: oversize title + item count meta (like an editorial index page).
  if (layout === 'masonry-editorial') {
    const imgs = (c.images || []).length ? c.images : [
      'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1000&q=80',
      'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1000&q=80',
      'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1000&q=80',
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1000&q=80',
      'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1000&q=80',
      'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1000&q=80',
      'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1000&q=80',
      'https://images.unsplash.com/photo-1467043153537-a4fba2cd39ef?w=1000&q=80',
    ];
    // Assign a pseudo-random but stable aspect ratio per index for editorial variety.
    const aspects = ['aspect-[3/4]', 'aspect-[4/5]', 'aspect-square', 'aspect-[5/6]', 'aspect-[4/3]'];
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }} data-block-type={block.type} data-layout={block.layout}>
        <div className="max-w-6xl mx-auto flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: theme.accent }}>{c.eyebrow || 'THE ARCHIVE'}</p>
            <h2 className='mt-3 text-4xl md:text-6xl font-bold font-["Playfair_Display","Georgia",serif]' style={{ color: theme.ink }}>{c.title || 'Selected works.'}</h2>
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-60">{String(imgs.length).padStart(2, '0')} images</p>
        </div>
        <div className="max-w-6xl mx-auto columns-1 md:columns-2 lg:columns-3 gap-5">
          {imgs.map((src, i) => (
            <div key={i} className={`mb-5 break-inside-avoid overflow-hidden ${aspects[i % aspects.length]}`} style={{ borderRadius: theme.radius || 6 }}>
              <SafeImg src={typeof src === 'string' ? src : src.src} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Gallery — NEW `full-bleed-scroll` ──
  // Horizontal auto-scroll strip of oversized full-height images. Perfect
  // for photographers/craft brands wanting immersive scale.
  if (layout === 'full-bleed-scroll') {
    const imgs = (c.images || []).length ? c.images : [
      'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1600&q=80',
      'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1600&q=80',
      'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1600&q=80',
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600&q=80',
      'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=80',
    ];
    return (
      <section className="py-16" style={{ background: theme.bg, color: theme.ink }} data-block-type={block.type} data-layout={block.layout}>
        <div className="max-w-6xl mx-auto px-6 mb-10">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: theme.accent }}>{c.eyebrow || 'IN FRAME'}</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold" style={{ fontFamily: theme.headingFont, color: theme.ink }}>{c.title || 'Scroll the story.'}</h2>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-6 pl-6 pr-6 snap-x snap-mandatory" style={{ scrollbarWidth: 'thin' }}>
          {imgs.map((src, i) => (
            <div key={i} className="shrink-0 snap-start overflow-hidden" style={{ width: 'min(80vw, 720px)', aspectRatio: '4 / 5', borderRadius: theme.radius || 6 }}>
              <SafeImg src={typeof src === 'string' ? src : src.src} alt="" className="w-full h-full object-cover" />
              <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest opacity-60">
                <span>0{i + 1}</span>
                <span>{c.caption_prefix || 'Frame'}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return null;
}
